import * as React from "react";
import {
  Link as RouterLink,
  type LinkProps as RouterLinkProps,
} from "react-router";

type KeysOfUnion<T> = T extends unknown ? keyof T : never;

// A plain union only rejects a prop that no member declares — `target` without
// `href` would still type-check, landing on a `<button>`. This gives each
// member a `?: never` for every prop only the other members declare, so a prop
// commits the whole object to its shape. The `never` fields on `to` / `href`
// are also what `props.to !== undefined` narrows on.
export type ExclusiveUnion<T, All = T> = T extends unknown
  ? T & Partial<Record<Exclude<KeysOfUnion<All>, keyof T>, never>>
  : never;

// `Omit` over a union keeps only the keys all members share, which would erase
// the shapes; this applies it to each member instead.
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

// All three shapes are built from the bare attribute types, like react-router's
// `LinkProps`. `ComponentPropsWithoutRef` would add React's `key` to the other
// two, and `ExclusiveUnion` would then forbid `key` on a router link.
//
// `type` is narrowed to what a button in a form means by it, and dropped from
// the link shapes, where it is a MIME hint nobody here wants. The link shapes
// also end up without `disabled`: `:disabled` never matches an `<a>`, and a link
// that only looks disabled is still followable.
type NativeButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> & { type?: "button" | "submit" };

type RouterLinkButtonProps = Omit<RouterLinkProps, "type">;

type AnchorButtonProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "type"
> & { href: string };

/**
 * `to` → react-router's `<Link>`, `href` → a plain `<a>`, neither → a
 * `<button>`.
 */
export type ButtonOrLinkProps = ExclusiveUnion<
  NativeButtonProps | RouterLinkButtonProps | AnchorButtonProps
>;

/**
 * Internal base for `Button` and `IconButton`. Callers pass the same
 * `className` and children whichever element comes out, so a link and a button
 * look alike by construction — the recipes take care of the few properties
 * `<a>` and `<button>` get from different defaults.
 */
export const ButtonOrLink = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonOrLinkProps
>((props, ref) => {
  if (props.to !== undefined) {
    return <RouterLink {...props} ref={ref as React.Ref<HTMLAnchorElement>} />;
  }

  if (props.href !== undefined) {
    const {
      rel = props.target === "_blank" ? "noopener noreferrer" : undefined,
      ...anchorProps
    } = props;
    return (
      <a {...anchorProps} rel={rel} ref={ref as React.Ref<HTMLAnchorElement>} />
    );
  }

  const { type = "button", ...buttonProps } = props;
  return (
    <button
      {...buttonProps}
      type={type}
      ref={ref as React.Ref<HTMLButtonElement>}
    />
  );
});
